/**
 * Phase 3 forms (docs/04): Parts List, Field Report, Field Lube Report, Rental Check-Out/In and
 * signatures. Request bodies shared by the office API, the iPad's sync items and the web app.
 *
 * Numbers that end up in DECIMAL columns are validated as decimal strings (a JSON number is
 * accepted only if it prints exactly, e.g. 2.5 — never 0.30000000000000004), so the server can
 * build a Prisma.Decimal from the string and money never passes through a float.
 */
import { z } from 'zod';
export const SIGNATURE_FORMS = ['FIELD_REPORT', 'LUBE', 'RENTAL_OUT', 'RENTAL_IN'];
export const SignatureForm = z.enum(SIGNATURE_FORMS);
export const SIGNATURE_ROLES = ['TECHNICIAN', 'CUSTOMER'];
export const SignatureRole = z.enum(SIGNATURE_ROLES);
export const SIGNATURE_FORM_LABELS = {
    FIELD_REPORT: 'Field Report',
    LUBE: 'Field Lube Report',
    RENTAL_OUT: 'Rental Check-Out',
    RENTAL_IN: 'Rental Check-In',
};
export const RENTAL_PHASES = ['OUT', 'IN'];
export const CLEANLINESS = ['CLEAN', 'ADDITIONAL_TIME'];
export const CLEANLINESS_LABELS = {
    CLEAN: 'Clean',
    ADDITIONAL_TIME: 'Additional time required',
};
export const DAMAGE_BILLING = ['BILLED', 'WAIVED'];
/** Lubricant rows not in the profile list use this name plus `otherName` (paper: "Other"). */
export const OTHER_LUBRICANT = 'Other';
/** The cleanliness line in the rental damage list (it is not an inspection item). */
export const CLEANLINESS_ITEM = 'Cleanliness';
/** Photo slots for one rental inspection: the profile's required slots + one per attachment. */
export function rentalPhotoSlots(requiredSlots, attachments) {
    return [...requiredSlots, ...(attachments ?? []).map((a) => `Attachment: ${a}`)];
}
/** Which rental phase a job type records, from the profile's closeRules (D16). */
export function rentalPhaseFor(jobType, rules) {
    if (rules.rentalCheckOutJobTypes.includes(jobType))
        return 'OUT';
    if (rules.rentalCheckInJobTypes.includes(jobType))
        return 'IN';
    return null;
}
/** The form whose signatures satisfy the close checks for this job type (D24). */
export function signatureFormFor(jobType, rules) {
    const phase = rentalPhaseFor(jobType, rules);
    if (phase === 'OUT')
        return 'RENTAL_OUT';
    if (phase === 'IN')
        return 'RENTAL_IN';
    if (rules.lubeJobTypes.includes(jobType))
        return 'LUBE';
    return 'FIELD_REPORT';
}
// ---------- number helpers ----------
const decimal = (intDigits, frac, label) => z
    .union([z.string().trim(), z.number()])
    .transform((v) => String(v))
    .pipe(z.string().regex(new RegExp(`^\\d{1,${intDigits}}(\\.\\d{1,${frac}})?$`), `${label}: enter a number (up to ${frac} decimals)`));
/** Optional decimal: '' or null clears it. */
const optDecimal = (intDigits, frac, label) => z
    .union([z.literal(''), z.null(), decimal(intDigits, frac, label)])
    .optional()
    .transform((v) => (v === '' ? null : v));
const optText = (max) => z
    .string()
    .trim()
    .max(max)
    .nullable()
    .optional()
    .transform((v) => (v === undefined ? undefined : v ? v : null));
export const Money = (label) => optDecimal(10, 2, label);
export const Qty = decimal(8, 2, 'Qty').refine((v) => Number(v) > 0, 'Qty must be more than 0');
// ---------- Parts List ----------
/** What a mechanic enters (docs/04): qty, part no., # replaced, description. */
export const PartLineInput = z.object({
    qty: Qty,
    partNo: optText(80),
    replacedPartNo: optText(80),
    description: z.string().trim().min(1, 'Describe the part').max(300),
});
/** Office/Manager may also enter stock and prices. */
export const PartLineOfficeInput = PartLineInput.extend({
    stock: z.boolean().optional(),
    cost: Money('Cost'),
    list: Money('List'),
    sell: Money('Sell'),
});
export const PartLinePatch = PartLineOfficeInput.partial();
export const PartRemoveInput = z.object({ reason: z.string().trim().min(3, 'Say why the line is removed').max(2000) });
export const PartsMetaInput = z.object({
    purchaseOrder: optText(60),
    salesRep: optText(120),
    customerPo: optText(60),
    contactName: optText(120),
    contactPhone: optText(40),
});
export const NoPartsUsedInput = z.object({ value: z.boolean() });
// ---------- Field Report ----------
export const FieldReportInput = z.object({
    lubeEng: optText(60),
    lubeTrans: optText(60),
    lubeHyd: optText(60),
    lubeCoolant: optText(60),
    lubeOther: optText(120),
    mileage: optDecimal(9, 1, 'Mileage'),
    meals: Money('Meals'),
    motel: Money('Motel'),
});
// ---------- Field Lube Report ----------
export const LubeItemInput = z.object({ item: z.string().trim().min(1).max(80), checked: z.boolean(), changed: z.boolean() });
export const LubeLubricantInput = z.object({
    lubricant: z.string().trim().min(1).max(60),
    otherName: z.string().trim().max(80).optional().default(''),
    gallons: z.union([z.literal(''), decimal(8, 2, 'Gallons')]).transform((v) => (v === '' ? '0' : v)),
});
export const LubeSaveInput = z.object({
    customerEquipmentNo: optText(80),
    typeOfService: optText(120),
    nextService: optText(120),
    equipHours: optDecimal(9, 1, 'Hours'),
    remarks: optText(10_000),
    items: z.array(LubeItemInput).max(100).optional(),
    lubricants: z.array(LubeLubricantInput).max(100).optional(),
});
// ---------- Rental Check-Out / Check-In ----------
export const RentalItemInput = z.object({
    item: z.string().trim().min(1).max(80),
    status: z.enum(['OK', 'ISSUE']),
    comment: optText(2000),
});
export const RentalSaveInput = z.object({
    hours: optDecimal(9, 1, 'Machine hours'),
    fuelLevel: optText(20),
    fuelGallonsAdded: optDecimal(8, 2, 'Gallons added'),
    defLevel: optText(20),
    defJugsAdded: optDecimal(8, 2, 'Jugs added'),
    cleanliness: z.enum(CLEANLINESS).nullable().optional(),
    operatorsManual: z.boolean().nullable().optional(),
    attachments: z.array(z.string().trim().min(1).max(60)).max(10).optional(),
    initials: optText(10),
    notes: optText(10_000),
    items: z.array(RentalItemInput).max(50).optional(),
});
export const RentalLinkInput = z.object({
    agreementNo: optText(60),
    /** Check-in job only: pair with this rental (an open check-out on the same machine). */
    rentalId: z.number().int().positive().optional(),
});
/** Manager marks a new-damage line (an inspection item, or "Cleanliness") Billed or Waived. */
export const DamageBillingInput = z.object({
    item: z.string().trim().min(1).max(80),
    billing: z.enum(DAMAGE_BILLING),
});
// ---------- Signatures ----------
export const SignatureInput = z.object({
    form: SignatureForm,
    role: SignatureRole,
    /** Required for a customer; a mechanic's own signature uses their name. */
    signerName: z.string().trim().max(120).optional(),
});
/** Office capture: the PNG from the signature pad as a data URL. */
export const OfficeSignatureInput = SignatureInput.extend({
    image: z.string().regex(/^data:image\/png;base64,[A-Za-z0-9+/=]+$/, 'Signature image must be a PNG').max(900_000),
});
export const CustomerNotPresentInput = z.object({
    /** null clears it (e.g. the customer came back and signed). */
    reason: z.string().trim().min(10, 'Say why the customer could not sign (at least 10 characters)').max(2000).nullable(),
});
//# sourceMappingURL=forms.js.map